import React from 'react';
import { get, findIndex } from 'lodash-es';
import { ColorList } from 'site-modules/shared/components/color-list/color-list';
import {
  BASIC_MAPPING,
  OVERVIEW_MAPPING,
  EV_SECTION_DISPLAY_NAMES,
  FEATURE_DISPLAY_NAMES,
  OLD_SAFETY_FORMAT_FEATURE_LABELS,
  OLD_SAFETY_FORMAT_LAST_VEHICLE_YEAR,
  SECTION_DISPLAY_NAMES,
} from 'site-modules/features-specs/constants/features-specs';
import { getBedLengthValue } from 'site-modules/shared/utils/features-format';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { calculateTailpipeEmissions } from 'site-modules/shared/utils/range-and-cost';
import { DEFAULT_PERSONALIZATION_PARAMS } from 'site-modules/shared/constants/range-and-cost';

export function getFormattedFeature({ style, category, feature, isOldSafetyFormat }) {
  if (category === 'Colors') {
    if (feature.name === 'Exterior Colors' && get(style, 'color.EXTERIOR.length') > 0) {
      return {
        ...feature,
        value: (
          <ColorList
            className="text-start ms-0"
            colors={style.color.EXTERIOR.filter(({ rgb }) => rgb)}
            keyPrefix={`exterior-${style.id}-`}
          />
        ),
      };
    } else if (feature.name === 'Interior Colors' && get(style, 'color.INTERIOR.length') > 0) {
      return {
        ...feature,
        value: (
          <ColorList
            className="text-start ms-0"
            colors={style.color.INTERIOR.filter(({ rgb }) => rgb)}
            keyPrefix={`interior-${style.id}-`}
          />
        ),
      };
    }

    return null;
  }

  if (feature.name === 'Bed Length') {
    return { ...feature, value: getBedLengthValue(feature.value) };
  }

  if (category === 'Safety' && isOldSafetyFormat) {
    const value = OLD_SAFETY_FORMAT_FEATURE_LABELS[feature.name.slice(0, 1)];
    const name = feature.name.slice(1).trim();

    return name ? { ...feature, name, value } : null;
  }

  return feature;
}

export function checkIsOldSafetyFormat(year) {
  return parseInt(year, 10) <= OLD_SAFETY_FORMAT_LAST_VEHICLE_YEAR;
}

function getMapFeature(style, { topLevelKey, featureCategory, featureName }) {
  return (
    get(style, topLevelKey) ||
    (style.features.standard[featureCategory] || style.features?.optional?.[featureCategory])?.find(
      ({ name }) => name === featureName
    )
  );
}

export function getCustomMapFeatures(style, customMap) {
  return customMap
    .map(({ fallback: fallbackMap, ...map }) => {
      const feature = getMapFeature(style, map);
      const fallbackFeature = fallbackMap && getMapFeature(style, fallbackMap);

      if (!feature && !fallbackFeature) {
        return null;
      }

      const displayFeature = feature || fallbackFeature;
      const { label, description, valuePrefix } = feature ? map : fallbackMap;

      return typeof displayFeature === 'object'
        ? { ...displayFeature, name: label, description, valuePrefix }
        : { name: label, description, valuePrefix, value: displayFeature };
    })
    .filter(value => value);
}

export function includeAdditionalFeatures({ features, category, fuelCostsData, evTestedData, isPluginElectric }) {
  if (category !== 'Fuel') {
    return features;
  }

  let currentFeatures = features;

  const costPerMonth = get(fuelCostsData, 'fuelCosts.costPerMonth', 0);
  if (costPerMonth) {
    currentFeatures = [{ name: 'Cost to Drive', value: `${getPriceString(costPerMonth)}/mo` }, ...currentFeatures];
  }

  const evTestedRange = get(evTestedData, 'range');
  const evRangeIndex = findIndex(currentFeatures, ({ name }) => name.toLowerCase() === 'epa electricity range');
  const evTestedRangeIndex = evRangeIndex === -1 ? currentFeatures.length : evRangeIndex;
  if (evTestedRange) {
    currentFeatures.splice(evTestedRangeIndex, 0, { name: 'Edmunds Tested Range', value: `${evTestedRange} mi.` });
  }

  const mpgValue = get(
    features.find(({ name }) => ['epa city/highway mpg', 'epa city/highway mpge'].includes(name.toLowerCase())),
    'value',
    ''
  );
  const [city, highway] = mpgValue.match(/(\d+)/g) || [];
  const emissions = calculateTailpipeEmissions({
    primaryFuelGrade: get(fuelCostsData, 'fuelCosts.primary.fuelGrade') || get(fuelCostsData, 'fuelCosts.fuelGrade'),
    mpgData: { city, highway },
    ...(isPluginElectric
      ? {
          range: DEFAULT_PERSONALIZATION_PARAMS.dailyRange,
          dailyCommute: DEFAULT_PERSONALIZATION_PARAMS.dailyCommute,
        }
      : {}),
  });
  if (emissions) {
    currentFeatures.push({ name: 'CO2 Emissions', value: `${parseFloat(emissions.toFixed(1))} tons` });
  }

  return currentFeatures;
}

export function getOverviewMap({ truck, electric, pluginElectric }) {
  let map;
  switch (true) {
    case electric: {
      map = OVERVIEW_MAPPING.EV;
      break;
    }
    case pluginElectric: {
      map = OVERVIEW_MAPPING.PHEV;
      break;
    }
    default: {
      map = OVERVIEW_MAPPING.Gas;
    }
  }

  return [...map, ...BASIC_MAPPING[!!truck]];
}

export function getSectionAnchor(category, isElectric) {
  return {
    hash: `${category.replace(/\s+/g, '')}-section`,
    name: (isElectric ? EV_SECTION_DISPLAY_NAMES : SECTION_DISPLAY_NAMES)[category] || category,
  };
}

export function isMatchKeyword(name, keyword) {
  return (FEATURE_DISPLAY_NAMES[name] || name).toLowerCase().includes(keyword.toLowerCase());
}
